import type { QueryClient } from '@tanstack/react-query';
import {
  armConfirmedPositionTransition,
  CONFIRMED_POSITION_TRANSITION_WINDOW_MS,
  type ConfirmedPositionTransitionLogger,
  hasPendingConfirmedPositionTransitions,
  releaseExpiredConfirmedPositionTransitions,
} from '../core/internal/confirmed-position-transition.js';
import {
  type NormalizedEntityWriteMeta,
  type NormalizedEntityWriteResult,
  writeNormalizedEntitySnapshot,
} from '../core/internal/normalized-entity-write.js';
import { _bumpPerf } from '../devtool/perf-counters.js';
import { _observe } from '../devtool/perf-histograms.js';
import type { GetPositionsRow } from '../generated/openapi-types.gen.js';
import { hashDiagnosticEntity } from '../tx-engine/diagnostics/lifecycle.js';

export const POSITION_ENTITY_QUERY_KEY = 'positions';

export type PositionEntityLogger = ConfirmedPositionTransitionLogger;

export interface ApplyConfirmedPrizeClaimInput {
  readonly marketAddress: string;
  readonly walletAddress: string;
  readonly burnAmountD6: string;
  readonly network: string;
  readonly confirmedAtMs: number;
  readonly logger?: PositionEntityLogger;
}

export type ApplyConfirmedPrizeClaimResult =
  | { readonly status: 'applied'; readonly positionId: string }
  | { readonly status: 'already-applied' | 'amount-mismatch' | 'not-found' };

export {
  armConfirmedPositionTransition,
  CONFIRMED_POSITION_TRANSITION_WINDOW_MS,
  hasPendingConfirmedPositionTransitions,
  releaseExpiredConfirmedPositionTransitions,
};

export function positionEntityQueryKey(
  positionId: string,
): readonly [typeof POSITION_ENTITY_QUERY_KEY, 'entity', string] {
  return [POSITION_ENTITY_QUERY_KEY, 'entity', positionId];
}

export function readPositionEntity(
  queryClient: QueryClient,
  positionId: string,
): GetPositionsRow | undefined {
  return queryClient.getQueryData<GetPositionsRow>(positionEntityQueryKey(positionId));
}

/**
 * Apply a full confirmed prize claim to the canonical position immediately.
 * The confirmation fence prevents a lagging REST projection from restoring
 * the spent claim rights while the indexer catches up.
 */
export function applyConfirmedPrizeClaim(
  queryClient: QueryClient,
  input: ApplyConfirmedPrizeClaimInput,
): ApplyConfirmedPrizeClaimResult {
  const burnAmountD6 = parseCanonicalUnsigned(input.burnAmountD6);
  if (burnAmountD6 === null || burnAmountD6 === BigInt(0)) return { status: 'amount-mismatch' };

  let alreadyApplied = false;
  for (const query of queryClient.getQueryCache().getAll()) {
    if (query.queryKey[0] !== POSITION_ENTITY_QUERY_KEY || query.queryKey[1] !== 'entity') continue;
    const row = query.state.data as GetPositionsRow | undefined;
    if (row?.market !== input.marketAddress || row.wallet !== input.walletAddress) continue;
    if (row.prizeState === 'claimed' && row.claimRightsBalanceD6 === '0') {
      alreadyApplied = true;
      continue;
    }
    if (row.prizeState !== 'claimable') continue;

    const claimRightsBalanceD6 = parseCanonicalUnsigned(row.claimRightsBalanceD6);
    const prizeClaimableD6 = parseCanonicalUnsigned(row.prizeClaimableD6);
    const prizeClaimedD6 = parseCanonicalUnsigned(row.prizeClaimedD6);
    if (
      claimRightsBalanceD6 === null ||
      prizeClaimableD6 === null ||
      prizeClaimedD6 === null ||
      claimRightsBalanceD6 !== burnAmountD6
    ) {
      return { status: 'amount-mismatch' };
    }

    // The server breakdown still prices the claim rights and the prize; drop
    // it so no reader shows the claimed amount twice until REST refreshes.
    const claimedRow: GetPositionsRow = {
      ...row,
      claimRightsBalanceD6: '0',
      prizeClaimableD6: '0',
      prizeClaimedD6: (prizeClaimedD6 + prizeClaimableD6).toString(),
      prizeState: 'claimed',
      pnl: null,
      pnlUnavailableReason: 'optimistic_claim_pending',
    };
    setPositionEntity(
      queryClient,
      claimedRow,
      { source: 'optimistic', observedAtMs: input.confirmedAtMs },
      input.logger,
    );
    armConfirmedPositionTransition(queryClient, {
      row: claimedRow,
      direction: 'claim',
      network: input.network,
      confirmedAtMs: input.confirmedAtMs,
      indexQueryKeys: [],
      ...(input.logger === undefined ? {} : { logger: input.logger }),
    });
    return { status: 'applied', positionId: row.positionId };
  }

  return { status: alreadyApplied ? 'already-applied' : 'not-found' };
}

export function setPositionEntity(
  queryClient: QueryClient,
  row: GetPositionsRow,
  meta: NormalizedEntityWriteMeta,
  logger?: PositionEntityLogger,
): NormalizedEntityWriteResult {
  if (row.positionId.length === 0) throw new Error('Position entity requires a positionId');
  const result = writeNormalizedEntitySnapshot(
    queryClient,
    positionEntityQueryKey(row.positionId),
    row,
    meta,
  );
  if (result.status === 'stale') {
    logger?.warn('position_entity.stale_write_rejected', {
      positionHash: hashDiagnosticEntity(row.positionId),
      incomingSource: meta.source,
      incomingObservedAtMs: meta.observedAtMs,
      cachedSource: result.previousSource,
      cachedObservedAtMs: result.previousObservedAtMs,
    });
  }
  return result;
}

export function recordPositionEntityIndexMoves(count: number): void {
  if (!Number.isSafeInteger(count) || count <= 0) return;
  if (process.env.NODE_ENV !== 'production') {
    _bumpPerf('positionEntity.indexMoves', count);
  }
}

export function observePositionReconcileLatency(durationMs: number): void {
  if (process.env.NODE_ENV !== 'production') {
    _observe('positionReconcileDurationMs', durationMs);
  }
}

function parseCanonicalUnsigned(value: string): bigint | null {
  return /^(0|[1-9]\d*)$/.test(value) ? BigInt(value) : null;
}
